import { SubscriptionChangeResultDTO } from 'api-models'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { ProductAccounts } from '../services/user'
import { parseError } from '../utils/errors'
import { useStores } from './useStores'

export type ChangeSubscriptionPhase =
	| 'idle'
	| 'submitting'
	| 'verifying_3ds'
	| 'finalizing'

// cancelled / failed both leave `error` set for the caller to render.
export type ChangePlanOutcome =
	| { status: 'succeeded' }
	| { status: 'processing' }
	| { status: 'cancelled' }
	| { status: 'failed' }

interface ChangePlanArgs {
	accountId: string
	tier: string
	priceId: string
	couponCode: string | null
	scheduleSubscriptionChange: boolean
}

const SUBMIT_LABELS: Record<ChangeSubscriptionPhase, string> = {
	idle: 'Update now',
	submitting: 'Processing...',
	verifying_3ds: 'Waiting for authentication...',
	finalizing: 'Finalizing...'
}

/**
 * Shared upgrade-from-Active orchestration for the "Change subscription plan"
 * surfaces. Owns the phase state machine, inline 3DS, error sanitization, and the
 * post-success account refresh + toast. See docs/19-SUBSCRIPTION-CHANGE-FLOW.md.
 */
export const useChangeSubscription = () => {
	const { accountStore, billingStore } = useStores()
	const [phase, setPhase] = useState<ChangeSubscriptionPhase>('idle')
	const [error, setError] = useState<string | null>(null)

	const runThreeDSChallenge = async (
		accountId: string,
		clientSecret: string,
		paymentIntentId: string
	): Promise<SubscriptionChangeResultDTO | null> => {
		setPhase('verifying_3ds')
		const stripe = await billingStore.stripe
		if (!stripe) {
			setError(
				'Payment service is unavailable. Please refresh the page and try again.'
			)
			return null
		}
		const next = await stripe.handleNextAction({ clientSecret })
		if (next.error) {
			// Stripe gives no structured cancel signal — match its message text.
			const rawMsg = next.error.message ?? 'Authentication failed'
			const looksCancelled = /cancel|abandon|dismiss/i.test(rawMsg)
			setError(
				looksCancelled
					? 'Authentication was cancelled — please try again.'
					: rawMsg
			)
			return null
		}
		setPhase('finalizing')
		return ProductAccounts.changeSubscription(accountId, {
			payment_intent_id: paymentIntentId
		})
	}

	const changePlan = async (
		args: ChangePlanArgs
	): Promise<ChangePlanOutcome> => {
		setPhase('submitting')
		setError(null)
		try {
			let result = await ProductAccounts.changeSubscription(args.accountId, {
				tier: args.tier,
				price_id: args.priceId,
				coupon_code: args.couponCode,
				schedule_subscription_change: args.scheduleSubscriptionChange
			})

			if (result.kind === 'requires_action') {
				const post3ds = await runThreeDSChallenge(
					args.accountId,
					result.client_secret,
					result.payment_intent_id
				)
				if (!post3ds) return { status: 'cancelled' }
				if (post3ds.kind === 'requires_action') {
					setError(
						'Unexpected response after authentication, please try again.'
					)
					return { status: 'failed' }
				}
				result = post3ds
			}

			const processing = result.kind === 'processing'
			await Promise.all([
				accountStore.getSubscriptionByAccountId(args.accountId),
				accountStore.setAccountForId(args.accountId)
			])
			toast.success(
				processing
					? 'Your plan change is being processed — it will apply once your bank payment clears.'
					: 'Subscription updated'
			)
			return { status: processing ? 'processing' : 'succeeded' }
		} catch (err: any) {
			setError(
				parseError(err) || 'Failed to update subscription. Please try again.'
			)
			return { status: 'failed' }
		} finally {
			setPhase('idle')
		}
	}

	return {
		error,
		clearError: () => setError(null),
		isBusy: phase !== 'idle',
		submitLabel: SUBMIT_LABELS[phase],
		changePlan
	}
}
